/**
 * 프론트엔드 범용 유틸리티 함수들입니다.
 *
 * 주요 기능:
 * - API URL 생성 (getHostPort, getApiUrl)
 * - Map 불변 업데이트 (newMapWithValue, newMapWithValues)
 * - 스로틀 로거 생성 (createThrottledLogger)
 * - 배치 객체 업데이트 (cloneWithBatchUpdate)
 *
 * @remarks
 * React 상태 업데이트, API 연결 등 다양한 상황에서 활용됩니다.
 */
import isArray from 'lodash-es/isArray';
import isMap from 'lodash-es/isMap';
import type { AppInfo } from '@bit-box/common';
import isObject from 'lodash-es/isObject';
import toPath from 'lodash-es/toPath';

// !deprecated 객체 비교 함수 (두 객체의 키/값이 동일한지 체크)
// todo: 2024-04-11 buruss lodash의 isEqual 함수로 대체하여 아래 함수는 차후 삭제 예정입니다. 필요하면 알려주세요.
export function __isObjectEqual(obj1: unknown, obj2: unknown): boolean {
  // console.log(`isObjectEqual start`);

  // console.log('isObjectEqual 1', obj1);
  // console.log('isObjectEqual 2', obj2);

  // 객체인지 확인하는 함수
  const isObject = (obj: unknown) => obj !== null && typeof obj === 'object';

  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const o1 = obj1 as Record<string, unknown>;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const o2 = obj2 as Record<string, unknown>;
  const obj1Keys = Object.keys(o1);
  const obj2Keys = Object.keys(o2);

  // 객체 길이 비교
  if (obj1Keys.length !== obj2Keys.length) {
    // console.log(`isObjectEqual length\n\t\t\tobj1=${obj1Keys.length}\n\t\t\tobj2=${obj2Keys.length}`);
    // console.log(`isObjectEqual end. false`);
    return false;
  }

  // key 비교
  for (const key of obj1Keys) {
    if (!obj2Keys.includes(key)) {
      // console.log(`isObjectEqual obj2Keys key=${key}`);
      // console.log(`isObjectEqual end. false`);
      return false;
    }
  }
  for (const key of obj2Keys) {
    if (!obj1Keys.includes(key)) {
      // console.log(`isObjectEqual obj1Keys key=${key}`);
      // console.log(`isObjectEqual end. false`);
      return false;
    }
  }

  // value 비교
  for (const key of obj1Keys) {
    const isObj =
      (isObject(o1[key]) && isObject(o2[key])) ||
      (isArray(o1[key]) && isArray(o2[key])) ||
      (isMap(o1[key]) && isMap(o2[key]));

    // console.log(`isObj = ${isObj}`);
    // console.log('val', o1[key]);

    if (isObj) {
      // console.log(`isObjectEqual parentKey. ${key}`);
      const equal = __isObjectEqual(o1[key], o2[key]);
      // console.log(`isObjectEqual key=${key}, equal=${equal}`);
      if (!equal) {
        return false;
      }
    } else {
      if (o1[key] !== o2[key]) {
        // console.log(`isObjectEqual ${key}\n\t\t\t${o1[key]}\n\t\t\t${o2[key]}`);
        // console.log(`isObjectEqual end. false`);
        return false;
      }
      // console.log(`isObjectEqual valueCheck ${key}\n\t\t\t${o1[key]}\n\t\t\t${o2[key]}`);
    }
  }

  // console.log(`isObjectEqual end. true`);
  return true;
}

/**
 * Electron인 경우 localhost:<apiPort>를 반환,
 * 아닌 경우 {window.location.hostname}:{window.location.port + botId}를 반환
 * @param electronAppInfo
 * @returns
 */
export function getHostPort(electronAppInfo?: AppInfo) {
  const thisBotId = Number(electronAppInfo?.botId || sessionStorage?.getItem('botId') || 0);
  // Electron인 경우에는 항상 localhost로 연결하고 포트는 electronAPI.getAppInfo().apiPort로 설정함.
  return electronAppInfo
    ? `localhost:${electronAppInfo.apiPort}`
    : `${window.location.hostname}:${Number(window.location.port) + Number(thisBotId)}`;
}

export function getApiUrl(electronAppInfo?: AppInfo) {
  // cloud 모드: sessionStorage의 engineUrl 우선 사용
  if (typeof window !== 'undefined') {
    const engineUrl = sessionStorage.getItem('engineUrl');
    if (engineUrl) {
      return `${engineUrl}/trpc`;
    }
  }
  return `http://${getHostPort(electronAppInfo)}/trpc`;
}

/**
 * 기존 Map에 새로운 값을 추가하여 새로운 Map을 반환
 * @param oldMap 기존 Map
 * @param key 추가할 키
 * @param value 추가할 값
 * @returns 새로운 Map
 */
export function newMapWithValue<K, V>(oldMap: Map<K, V>, key: K, value: V) {
  const newMap = new Map(oldMap);
  newMap.set(key, value);
  return newMap;
}

/**
 * 기존 Map에 여러 값을 추가하여 새로운 Map을 반환
 * @param oldMap 기존 Map
 * @param values 추가할 값들
 * @returns 새로운 Map
 */
export function newMapWithValues<K, V>(oldMap: Map<K, V>, values: [K, V][]) {
  const newMap = new Map(oldMap);
  values.forEach(([key, value]) => {
    newMap.set(key, value);
  });
  return newMap;
}

/**
 * 로그 출력을 제한하는 함수를 생성
 * @param intervalMs 로그 출력 간격 (기본값: 60000ms)
 * @returns 제한된 로그 출력 함수
 */
export function createThrottledLogger(intervalMs = 60000) {
  const lastLogTimes = new Map();

  /**
   * @param {string} key 로그 구분 키
   * @param {'log'|'error'|'warn'|'info'|'debug'} method console 메서드
   * @param  {...any} args 출력할 인자
   */
  return function throttledLog(
    key: string,
    method: 'log' | 'error' | 'warn' | 'info' | 'debug' = 'log',
    ...args: unknown[]
  ) {
    const now = Date.now();
    const lastLogTime = lastLogTimes.get(key) || 0;

    if (now - lastLogTime >= intervalMs) {
      (console[method] || console.log)(...args);
      lastLogTimes.set(key, now);
    }
  };
}

type PathSeg = string | number;
type Path = PathSeg[] | string;

export type BatchUpdateItem = { path: Path; value: unknown } | { path: Path; updater: (prev: unknown) => unknown };

type Indexable = Record<PropertyKey, unknown>;
const isIndexable = (v: unknown): v is Indexable => v !== null && (typeof v === 'object' || typeof v === 'function');

/**
 * 배치 set (중간 노드 1회 얕은 복사)
 *    복잡한 객체의 하위 여러 속성들을 반복 수정하되, React 용이므로 복사해야 하는 상황에서 사용.
 *    base 객체 전체 복사하는 것이 아니라 중간 경로에 있는 객체만 복사하도록 함.
 *    여러 업데이트에 의해서 중간 경로에 있는 객체가 여러번 반복 복제되지 않고 1회만 복제되도록 함.
 * @param base 대상 객체
 * @param updates 업데이트 배열
 * @returns 업데이트된 객체
 */
export function cloneWithBatchUpdate<T>(base: T, updates: BatchUpdateItem[]): T {
  if (!updates.length || !isObject(base)) return base;
  const clones = new WeakMap<object, unknown>();

  const ensureClone = (node: unknown) => {
    if (!isIndexable(node)) return node;
    const hit = clones.get(node);
    if (hit) return hit;
    // 배열/객체 모두 얕은 복사
    const copy = Array.isArray(node) ? node.slice() : { ...(node as object) };
    clones.set(node, copy);
    return copy;
  };

  const applyOne = (pathLike: Path, spec: Omit<BatchUpdateItem, 'path'>) => {
    const path = toPath(pathLike) as (string | number)[];
    const nodes: unknown[] = [base];
    const keys: (string | number)[] = [];

    // 1) 경로 따라 내려가되, 아직 복제는 안 함(지연)
    let curr: unknown = base;
    for (let i = 0; i < path.length; i++) {
      const k = path[i];
      keys.push(k);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      let next = isObject(curr) && (k as PropertyKey) in curr ? (curr as Indexable)[k as PropertyKey] : undefined;
      if (next === undefined) {
        // 다음 세그먼트가 숫자면 배열, 아니면 객체
        const nextIsIndex = typeof path[i + 1] === 'number';
        next = nextIsIndex ? [] : {};
      }
      nodes.push(next);
      curr = next;
    }

    // 2) 리프 계산 — 이전 업데이트 결과가 반영된 clone에서 prevLeaf를 읽음
    const origParent = nodes[nodes.length - 2];
    const leafKey = keys[keys.length - 1];
    const effectiveParent = isIndexable(origParent) ? (clones.get(origParent) ?? origParent) : origParent;
    const prevLeaf = isIndexable(effectiveParent) ? effectiveParent[leafKey] : undefined;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const nextLeaf = 'value' in spec ? spec.value : (spec as { updater: (prev: unknown) => unknown }).updater(prevLeaf);

    // 3) 아래→위 복제-연결(각 노드 최대 1회 복제)
    let child = nextLeaf;
    for (let i = nodes.length - 2; i >= 0; i--) {
      const pOrig = nodes[i];
      const k = keys[i];
      const pClone = ensureClone(pOrig);
      if (k !== undefined && isIndexable(pClone)) {
        pClone[k] = child;
      }
      child = pClone;
    }
  };

  for (const u of updates) applyOne(u.path, u);
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return (clones.get(base) ?? base) as T;
}
